import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Loader2, ArrowLeft, Sparkles, X, LogIn } from 'lucide-react'
import PageTransition from '@/components/PageTransition'
import GlassCard from '@/components/GlassCard'
import ScoreRing from '@/components/ScoreRing'
import CategoryAccordion from '@/components/CategoryAccordion'
import EmptyIllustration from '@/components/EmptyIllustration'
import { useAnonymousAuditProgress } from '@/hooks/useAnonymousAuditProgress'
import { authApi } from '@/api/client'

const BANNER_DISMISS_KEY = 'lens:anonymous-banner-dismissed'

export default function AnonymousAuditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { phase, progress, message, result } = useAnonymousAuditProgress(id)

  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem(BANNER_DISMISS_KEY) === '1',
  )
  const dismissBanner = () => {
    sessionStorage.setItem(BANNER_DISMISS_KEY, '1')
    setBannerDismissed(true)
  }

  return (
    <PageTransition>
      <div className="max-w-3xl mx-auto px-4 py-12">

        {phase !== 'complete' && phase !== 'error' && (
          <GlassCard className="text-center space-y-6">
            <div className="flex justify-center">
              <Loader2 size={40} className="animate-spin text-accent" />
            </div>
            <div>
              <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                {message || 'Preparing audit…'}
              </p>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden max-w-xs mx-auto">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: 'var(--accent)' }}
                  animate={{ width: `${progress}%` }}
                  transition={{ ease: 'linear', duration: 0.3 }}
                />
              </div>
              <p className="text-xs mt-2 tabular-nums" style={{ color: 'var(--text-muted)' }}>
                {Math.round(progress)}%
              </p>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Demo audit · won't be saved
            </p>
          </GlassCard>
        )}

        {phase === 'error' && (
          <GlassCard className="text-center space-y-4 py-10">
            <EmptyIllustration variant="error" size={104} className="mx-auto text-[var(--text-secondary)]" />
            <p className="text-red-400 font-medium">{message || 'Audit failed.'}</p>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 mx-auto text-sm transition-colors"
              style={{ color: 'var(--text-secondary)' }}
            >
              <ArrowLeft size={14} /> Try again
            </button>
          </GlassCard>
        )}

        {phase === 'complete' && result && (
          <div className="space-y-4">

            {!bannerDismissed && (
              <GlassCard className="relative py-4 border-accent/20 bg-accent/[0.04]">
                <button
                  onClick={dismissBanner}
                  className="absolute top-2 right-2 p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-white/5"
                  aria-label="Dismiss"
                >
                  <X size={14} />
                </button>
                <div className="flex items-start gap-3 pr-6">
                  <Sparkles size={18} className="text-accent shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1 text-[var(--text-primary)]">
                      Sign in to save this report
                    </p>
                    <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                      Free 14-day trial. Get AI-powered improvement suggestions,
                      compare audits over time, and keep all your reports in one place.
                    </p>
                    <a
                      href={authApi.googleSignInUrl()}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md
                                 bg-white text-slate-900 text-xs font-medium
                                 hover:bg-slate-100 transition-colors"
                    >
                      <LogIn size={13} />
                      Sign in with Google
                    </a>
                  </div>
                </div>
              </GlassCard>
            )}

            <div className="flex items-center justify-between mb-2 gap-2">
              <button
                onClick={() => navigate('/')}
                className="text-sm flex items-center gap-1 transition-colors"
                style={{ color: 'var(--text-secondary)' }}
              >
                <ArrowLeft size={14} /> New audit
              </button>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Demo · not saved
              </span>
            </div>

            <GlassCard>
              <p className="text-xs mb-1 truncate" style={{ color: 'var(--text-muted)' }}>
                {new Date().toLocaleString()}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 justify-items-center">
                <ScoreRing score={result.performanceScore}    label="Performance" />
                <ScoreRing score={result.accessibilityScore}  label="Accessibility" />
                <ScoreRing score={result.bestPracticesScore}  label="Best Practices" />
                <ScoreRing score={result.seoScore}            label="SEO" />
              </div>
            </GlassCard>

            {result.rawReport && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider px-1 mb-3"
                    style={{ color: 'var(--text-muted)' }}>
                  Audit Details
                </h3>
                <CategoryAccordion rawReport={result.rawReport} />
              </div>
            )}
          </div>
        )}
      </div>
    </PageTransition>
  )
}
